import { z } from "zod";

/**
 * Duplicate-prevention rules — docs/01-PRODUCT-SPEC.md §7, docs/03-API-SPEC.md §5.
 *
 * The evaluator these describe has existed since Phase 7 and works; what was missing was any way
 * to write a rule down. Every form in the database had zero rules, so nothing was ever blocked:
 * the live Edo form took 466 submissions from 447 people, and 18 of them applied more than once.
 *
 * The vocabularies below are the closed sets the evaluator switches on. They live here rather than
 * beside the evaluator because the builder screen has to offer exactly these and no others — a
 * strategy the server does not implement would save happily and then silently never match.
 */

/** How a rule decides two submissions are the same person. */
export const DEDUPE_STRATEGIES = [
  "one_per_respondent_per_cycle",
  "composite_key",
  "phone_unique",
] as const;
export type DedupeStrategy = (typeof DEDUPE_STRATEGIES)[number];

/** How wide the net is cast when looking for an earlier submission. */
export const DEDUPE_SCOPES = [
  "form_cycle",
  "form_all_cycles",
  "programme_cycle",
  "global_cycle",
] as const;
export type DedupeScope = (typeof DEDUPE_SCOPES)[number];

/** What happens when a rule matches. */
export const DEDUPE_ACTIONS = ["block", "warn_and_allow", "replace_previous"] as const;
export type DedupeAction = (typeof DEDUPE_ACTIONS)[number];

/**
 * Only `composite_key` keys on named fields; the other two derive their key from the respondent or
 * from a single phone field. Enforced here so the builder cannot save a composite rule that keys on
 * nothing — which would match every submission with a complete answer set, or none at all.
 */
export const dedupeRuleSchema = z
  .object({
    /**
     * Present for a rule that already exists, absent for a new one.
     *
     * Load-bearing, not cosmetic: the database guarantee is a unique index on
     * `submission_dedupe_keys (ruleId, keyHash)`, so a rule that is deleted and recreated gets a
     * new id, orphans every key written under the old one, and quietly starts letting duplicates
     * through. Keeping the id is what keeps the constraint continuous.
     */
    id: z.string().min(1).optional(),
    strategy: z.enum(DEDUPE_STRATEGIES),
    fieldKeys: z.array(z.string().min(1).max(70)).max(10).default([]),
    scope: z.enum(DEDUPE_SCOPES).default("form_cycle"),
    action: z.enum(DEDUPE_ACTIONS).default("block"),
    /** Shown to the applicant when the rule matches. Null falls back to the generic wording. */
    message: z.string().trim().max(500).nullish(),
    isActive: z.boolean().default(true),
  })
  .superRefine((rule, ctx) => {
    if (rule.strategy === "composite_key" && rule.fieldKeys.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["fieldKeys"],
        message: "Choose at least one field for a composite-key rule",
      });
    }
    if (rule.strategy === "phone_unique" && rule.fieldKeys.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["fieldKeys"],
        message: "Choose exactly one phone field",
      });
    }

    /**
     * A composite-key rule can only refuse.
     *
     * The evaluator finds an earlier submission for this strategy by looking up
     * `submission_dedupe_keys (ruleId, keyHash)`, and `writeDedupeKeys` writes those rows for
     * `block` rules only — deliberately, because the table's unique index *is* the block, so a row
     * written for a "warn" rule would make the next submission fail the constraint and be refused,
     * which is the opposite of what the rule says.
     *
     * The consequence is that a composite rule set to warn or replace can never match anything: no
     * keys are written, so the lookup always misses. It would save, display as active, and quietly
     * do nothing — CLAUDE.md's "a duplicate check with no matching database constraint behind it",
     * arrived at by configuration rather than by code. Refused here so nobody can reach that state
     * from the builder.
     *
     * `one_per_respondent_per_cycle` and `phone_unique` query submissions directly, so every action
     * is honest for them.
     */
    if (rule.strategy === "composite_key" && rule.action !== "block") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["action"],
        message:
          "A rule that matches on answers can only refuse the submission. Warning or replacing " +
          "needs one of the other rule types.",
      });
    }
  });

export type DedupeRule = z.infer<typeof dedupeRuleSchema>;

/**
 * The whole list, in order — `position` is the array index rather than a field anyone types.
 *
 * Order is what decides the outcome: §7 evaluates rules in order and returns the first match, so an
 * earlier `warn_and_allow` must not be allowed to mask a later `block`.
 */
export const dedupeRulesBodySchema = z.object({
  rules: z.array(dedupeRuleSchema).max(10),
});

export type DedupeRulesBody = z.infer<typeof dedupeRulesBodySchema>;
